import { useEffect, useState, useCallback } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi } from "../../lib/api";
import { inputCls, labelCls } from "./shared";

const EMPTY_BANNER = {
  slug: "", placement: "b2c_home_hero", title: "", subtitle: "", image_url: "",
  cta_text: "", cta_url: "", priority: 0, start_at: "", end_at: "", active: true,
};
const EMPTY_SEASON = {
  slug: "", title: "", hero_headline: "", hero_subtext: "", banner_image_url: "",
  cta_text: "Search flights", airlines: [], routes: [], offer_rules: {},
  default_dep: "KHI", default_ret: "DXB", start_at: "", end_at: "",
  seo_title: "", seo_description: "", published: true, sort_order: 0,
};

export function SimpleCrudTab({ kind }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [arraysText, setArraysText] = useState({ airlines: "", routes: "", offer_rules: "{}" });
  const base = kind === "banner" ? "/admin/banners" : "/admin/seasons";
  const empty = kind === "banner" ? EMPTY_BANNER : EMPTY_SEASON;

  const load = useCallback(() => {
    adminApi().get(base).then((r) => setItems(r.data)).catch(() => {});
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (doc, newDoc = false) => {
    setForm(doc);
    setIsNew(newDoc);
    if (kind === "season") {
      setArraysText({
        airlines: (doc.airlines || []).join(", "),
        routes: (doc.routes || []).join(", "),
        offer_rules: JSON.stringify(doc.offer_rules || {}, null, 2),
      });
    }
  };

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const remove = async (slug) => {
    if (!window.confirm(`Delete ${slug}?`)) return;
    await adminApi().delete(`${base}/${slug}`);
    toast.success("Deleted");
    load();
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (kind === "season") {
        payload.airlines = arraysText.airlines.split(",").map((s) => s.trim()).filter(Boolean);
        payload.routes = arraysText.routes.split(",").map((s) => s.trim()).filter(Boolean);
        payload.offer_rules = JSON.parse(arraysText.offer_rules || "{}");
        payload.sort_order = +form.sort_order || 0;
      } else {
        payload.priority = +form.priority || 0;
      }
      if (isNew) await adminApi().post(base, payload);
      else await adminApi().put(`${base}/${form.slug}`, payload);
      toast.success("Saved");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed — check JSON in offer_rules");
    }
  };

  const bannerFields = ["slug", "placement", "title", "subtitle", "image_url", "cta_text", "cta_url", "priority", "start_at", "end_at"];
  const seasonFields = ["slug", "title", "hero_headline", "hero_subtext", "banner_image_url", "default_dep", "default_ret", "cta_text", "start_at", "end_at", "sort_order", "seo_title", "seo_description"];
  const fields = kind === "banner" ? bannerFields : seasonFields;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          data-testid={`admin-add-${kind}-btn`}
          onClick={() => openEdit(empty, true)}
          className="flex items-center gap-2 bg-[#970C10] hover:bg-[#7A0A0D] text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors duration-200"
        >
          <Plus className="w-4 h-4" /> New {kind}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">{kind === "banner" ? "Placement" : "Dates"}</th>
              <th className="px-4 py-3">{kind === "banner" ? "Active" : "Published"}</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.slug} className="border-t border-slate-100" data-testid={`admin-${kind}-row-${it.slug}`}>
                <td className="px-4 py-3 font-mono text-xs">{it.slug}</td>
                <td className="px-4 py-3 font-semibold">{it.title}</td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {kind === "banner" ? it.placement : `${it.start_at || "—"} → ${it.end_at || "—"}`}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${(kind === "banner" ? it.active : it.published) ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {(kind === "banner" ? it.active : it.published) ? "Live" : "Off"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button data-testid={`admin-edit-${kind}-${it.slug}`} onClick={() => openEdit(it, false)} className="text-slate-500 hover:text-[#970C10] transition-colors duration-200">
                    <Pencil className="w-4 h-4 inline" />
                  </button>
                  <button data-testid={`admin-delete-${kind}-${it.slug}`} onClick={() => remove(it.slug)} className="text-slate-500 hover:text-red-600 transition-colors duration-200">
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No {kind}s yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`admin-${kind}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading">{isNew ? "Create" : "Edit"} {kind}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f} className={["subtitle", "hero_subtext", "seo_title", "seo_description", "image_url", "banner_image_url", "cta_url"].includes(f) ? "col-span-2" : ""}>
                  <label className={labelCls}>{f.replace(/_/g, " ")}</label>
                  {f === "placement" ? (
                    <select className={inputCls} value={form[f] ?? "b2c_home_hero"} onChange={set(f)} data-testid={`${kind}-placement-select`}>
                      <option value="b2c_home_hero">b2c_home_hero</option>
                      <option value="b2c_strip">b2c_strip</option>
                      <option value="b2b_home_hero">b2b_home_hero</option>
                    </select>
                  ) : ["start_at", "end_at"].includes(f) ? (
                    <input type="date" className={inputCls} value={form[f] ?? ""} onChange={set(f)} data-testid={`${kind}-${f}-input`} />
                  ) : (
                    <input className={inputCls} value={form[f] ?? ""} onChange={set(f)} disabled={f === "slug" && !isNew} data-testid={`${kind}-${f}-input`} />
                  )}
                </div>
              ))}
              {kind === "season" && (
                <>
                  <div className="col-span-2">
                    <label className={labelCls}>Featured airlines (comma-separated slugs)</label>
                    <input className={inputCls} value={arraysText.airlines} onChange={(e) => setArraysText({ ...arraysText, airlines: e.target.value })} placeholder="emirates, pia, saudia" data-testid="season-airlines-input" />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Featured routes (comma-separated slugs)</label>
                    <input className={inputCls} value={arraysText.routes} onChange={(e) => setArraysText({ ...arraysText, routes: e.target.value })} placeholder="karachi-to-dubai, lahore-to-jeddah" data-testid="season-routes-input" />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Offer rules override (JSON)</label>
                    <textarea className={`${inputCls} h-16 font-mono text-xs`} value={arraysText.offer_rules} onChange={(e) => setArraysText({ ...arraysText, offer_rules: e.target.value })} data-testid="season-offer-rules-input" />
                  </div>
                </>
              )}
              <label className="col-span-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={!!(kind === "banner" ? form.active : form.published)} onChange={set(kind === "banner" ? "active" : "published")} className="accent-[#970C10] w-4 h-4" data-testid={`${kind}-active-checkbox`} />
                {kind === "banner" ? "Active" : "Published"}
              </label>
            </div>
          )}
          <button onClick={save} data-testid={`${kind}-save-btn`} className="w-full bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold py-2.5 rounded-lg transition-colors duration-200">
            Save
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
